import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Search, MapPin, Globe, Star, Award, Loader, User, Briefcase, Shield, X, Eye } from 'lucide-react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { directoryApi, fileUrl } from '../api/client';
import './DirectoryPage.css';

const ROLE_OPTIONS = [
  { value: 'all', label: 'Tous', icon: User },
  { value: 'translator', label: 'Traducteurs', icon: Globe },
  { value: 'lawyer', label: 'Avocats', icon: Briefcase },
  { value: 'notary', label: 'Notaires', icon: Shield },
];

const ROLE_LABELS = {
  translator: 'Traducteur',
  lawyer: 'Avocat',
  notary: 'Notaire',
};

const ROLE_COLORS = {
  translator: 'var(--teal)',
  lawyer: 'var(--navy)',
  notary: 'var(--gold)',
};

const ROLE_HEX = {
  translator: '#1a7a7a',
  lawyer: '#0c1e3c',
  notary: '#c9a227',
};

// Les 24 gouvernorats tunisiens — coordonnées [lng, lat] du chef-lieu.
const TN_GOVERNORATES = [
  { name: 'Tunis', coords: [10.17, 36.8] },
  { name: 'Ariana', coords: [10.19, 36.87] },
  { name: 'Ben Arous', coords: [10.22, 36.75] },
  { name: 'Manouba', coords: [9.98, 36.81] },
  { name: 'Nabeul', coords: [10.74, 36.46] },
  { name: 'Zaghouan', coords: [10.14, 36.4] },
  { name: 'Bizerte', coords: [9.87, 37.27] },
  { name: 'Béja', coords: [9.18, 36.73] },
  { name: 'Jendouba', coords: [8.78, 36.5] },
  { name: 'Le Kef', coords: [8.7, 36.17] },
  { name: 'Siliana', coords: [9.37, 36.08] },
  { name: 'Kairouan', coords: [10.1, 35.68] },
  { name: 'Kasserine', coords: [8.84, 35.17] },
  { name: 'Sidi Bouzid', coords: [9.48, 35.04] },
  { name: 'Sousse', coords: [10.64, 35.83] },
  { name: 'Monastir', coords: [10.83, 35.76] },
  { name: 'Mahdia', coords: [11.06, 35.5] },
  { name: 'Sfax', coords: [10.76, 34.74] },
  { name: 'Gafsa', coords: [8.78, 34.42] },
  { name: 'Tozeur', coords: [8.13, 33.92] },
  { name: 'Kébili', coords: [8.97, 33.7] },
  { name: 'Gabès', coords: [10.1, 33.88] },
  { name: 'Médenine', coords: [10.51, 33.35] },
  { name: 'Tataouine', coords: [10.45, 32.93] },
];

const normCity = (s) =>
  (s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/^(le|la|el|al)\s+/i, '')
    .trim();

const findGov = (cityName) => {
  const c = normCity(cityName);
  if (!c) return null;
  return TN_GOVERNORATES.find((g) => c === normCity(g.name) || c.includes(normCity(g.name))) || null;
};

const initials = (name) =>
  (name || '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();

const escapeHtml = (s) =>
  String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

function langLabel(code) {
  const labels = {
    ar: 'Arabe', fr: 'Français', en: 'Anglais', it: 'Italien',
    de: 'Allemand', es: 'Espagnol', ru: 'Russe', pt: 'Portugais',
    tr: 'Turc', zh: 'Chinois', other: 'Autre',
  };
  return labels[code] || code?.toUpperCase();
}

const AVAIL_TONE = {
  disponible: 'ok', available: 'ok',
  partiellement_disponible: 'warn', en_mission: 'warn', sur_demande: 'warn', busy: 'warn',
  indisponible: 'off', unavailable: 'off', suspended: 'off',
};

const AVAIL_TEXT = {
  disponible: 'Disponible', available: 'Disponible',
  partiellement_disponible: 'Part. disponible', en_mission: 'En mission', sur_demande: 'Sur demande', busy: 'Occupé',
  indisponible: 'Indisponible', unavailable: 'Indisponible', suspended: 'Suspendu',
};

const availOf = (p) => {
  const v = p?.availability || 'disponible';
  return { label: AVAIL_TEXT[v] || v, tone: AVAIL_TONE[v] || 'ok' };
};

export default function DirectoryPage() {
  const [professionals, setProfessionals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    role: 'all',
    q: '',
    specialty: '',
    language: '',
    city: '',
    page: 0,
    limit: 12,
  });
  const [pagination, setPagination] = useState({
    page: 0,
    limit: 12,
    total: 0,
    totalPages: 0,
  });
  const [mapPros, setMapPros] = useState([]);
  const [selectedPro, setSelectedPro] = useState(null);
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markersRef = useRef(null);

  const debouncedFilters = useDebounce(filters, 350);

  // Carte Tunisie (Leaflet + tuiles OpenStreetMap) : init une seule fois
  useEffect(() => {
    if (!mapRef.current || mapInstance.current) return undefined;
    const map = L.map(mapRef.current, {
      center: [34.4, 9.6],
      zoom: 6,
      scrollWheelZoom: true,
      minZoom: 5,
      maxZoom: 12,
    });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
      maxZoom: 18,
    }).addTo(map);
    markersRef.current = L.layerGroup().addTo(map);
    mapInstance.current = map;
    setTimeout(() => map.invalidateSize(), 300);
    return () => {
      map.remove();
      mapInstance.current = null;
      markersRef.current = null;
    };
  }, []);

  // Professionnels en Tunisie pour la carte (limite haute, filtré par rôle)
  useEffect(() => {
    let cancelled = false;
    directoryApi.professionals({ role: filters.role, limit: 200 })
      .then((res) => { if (!cancelled) setMapPros(res.professionals || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [filters.role]);

  // Clic sur « Voir le profil » dans une popup de la carte
  useEffect(() => {
    const el = mapRef.current;
    if (!el) return undefined;
    const onClick = (e) => {
      const btn = e.target.closest('.dir-popup-btn');
      if (!btn) return;
      const pro = mapPros.find((p) => String(p.id) === String(btn.dataset.id));
      if (pro) {
        mapInstance.current?.closePopup();
        setSelectedPro(pro);
      }
    };
    el.addEventListener('click', onClick);
    return () => el.removeEventListener('click', onClick);
  }, [mapPros]);

  // Marqueurs circulaires par professionnel (style « consortium »)
  useEffect(() => {
    const layer = markersRef.current;
    if (!layer) return;
    layer.clearLayers();

    const byGov = new Map();
    mapPros.forEach((p) => {
      const gov = findGov(p.city);
      if (!gov) return;
      if (!byGov.has(gov.name)) byGov.set(gov.name, []);
      byGov.get(gov.name).push(p);
    });

    byGov.forEach((pros, govName) => {
      const gov = TN_GOVERNORATES.find((g) => g.name === govName);
      pros.forEach((p, i) => {
        // dispersion en spirale autour du chef-lieu pour éviter le chevauchement
        const angle = i * 2.4;
        const radius = i === 0 ? 0 : 0.08 + 0.05 * Math.floor(i / 6);
        const lat = gov.coords[1] + radius * Math.cos(angle);
        const lng = gov.coords[0] + radius * Math.sin(angle);
        const color = ROLE_HEX[p.role] || ROLE_HEX.translator;
        const inner = p.photoUrl
          ? `<img src="${fileUrl(p.photoUrl)}" alt="" style="width:100%;height:100%;object-fit:cover;border-radius:50%;"/>`
          : `<span>${escapeHtml(initials(p.name))}</span>`;
        const marker = L.marker([lat, lng], {
          icon: L.divIcon({
            className: 'dir-marker',
            html: `<div class="dir-marker-circle" style="background:linear-gradient(135deg, ${color}, ${color}dd);">${inner}</div>`,
            iconSize: [52, 52],
            iconAnchor: [26, 26],
          }),
        });
        marker.bindPopup(
          `<div class="dir-popup">
            <strong>${escapeHtml(p.name)}</strong>
            <span class="dir-popup-role" style="color:${color};">${escapeHtml(ROLE_LABELS[p.role] || p.role)}</span>
            ${p.city ? `<span class="dir-popup-city">📍 ${escapeHtml(p.city)}${p.country ? `, ${escapeHtml(p.country)}` : ''}</span>` : ''}
            <button type="button" class="dir-popup-btn" data-id="${escapeHtml(p.id)}" style="background:${color};">Voir le profil</button>
          </div>`,
        );
        marker.addTo(layer);
      });
    });
  }, [mapPros]);

  useEffect(() => {
    setLoading(true);
    setError('');
    directoryApi.professionals(debouncedFilters)
      .then((res) => {
        setProfessionals(res.professionals || []);
        setPagination(res.pagination || { page: 0, limit: 12, total: 0, totalPages: 0 });
      })
      .catch((err) => setError(err.data?.error || 'Impossible de charger l\'annuaire.'))
      .finally(() => setLoading(false));
  }, [debouncedFilters]);

  const uniqueOptions = useMemo(() => {
    const specialties = new Set();
    const languages = new Set();

    professionals.forEach((p) => {
      (p.specialties || []).forEach((s) => specialties.add(s));
      (p.langPairs || []).forEach((c) => {
        if (c.source) languages.add(c.source);
        if (c.target) languages.add(c.target);
      });
      (p.languages || []).forEach((l) => languages.add(l));
    });

    return {
      specialties: Array.from(specialties).sort(),
      languages: Array.from(languages).sort(),
    };
  }, [professionals]);

  const updateFilter = (key, value) => setFilters((f) => ({ ...f, [key]: value, ...(key !== 'page' && key !== 'limit' ? { page: 0 } : {}) }));

  const clearFilters = () => setFilters({
    role: 'all',
    q: '',
    specialty: '',
    language: '',
    city: '',
    page: 0,
    limit: 12,
  });

  const removeFilter = (key) => {
    const value = key === 'role' ? 'all' : '';
    updateFilter(key, value);
  };

  const activeFilters = useMemo(() => {
    const list = [];
    if (filters.role !== 'all') {
      const r = ROLE_OPTIONS.find((o) => o.value === filters.role);
      list.push({ key: 'role', value: filters.role, label: r?.label || filters.role });
    }
    if (filters.q) list.push({ key: 'q', value: filters.q, label: `“${filters.q}”` });
    if (filters.specialty) list.push({ key: 'specialty', value: filters.specialty, label: filters.specialty });
    if (filters.language) list.push({ key: 'language', value: filters.language, label: langLabel(filters.language) });
    if (filters.city) list.push({ key: 'city', value: filters.city, label: filters.city });
    return list;
  }, [filters]);

  return (
    <div className="directory-page">
      <header className="directory-hero">
        <img src="/images/hero-sworn-translation.jpg" alt="" className="directory-hero-bg" aria-hidden="true" />
        <div className="directory-hero-inner">
          <h1>Annuaire des <span>professionnels</span></h1>
          <p>Trouvez un traducteur, un avocat ou un notaire selon vos besoins.</p>
        </div>
      </header>

      <div className="directory-filter-bar">
        <div className="container">
          <div className="directory-filters">
            <div className="directory-filters-header">
              <div className="directory-tabs">
                {ROLE_OPTIONS.map(({ value, label, icon: Icon }) => (
                  <button
                    key={value}
                    type="button"
                    className={`directory-tab ${filters.role === value ? 'is-active' : ''}`}
                    onClick={() => updateFilter('role', value)}
                  >
                    <Icon size={16} />
                    {label}
                  </button>
                ))}
              </div>
              {activeFilters.length > 0 && (
                <button type="button" className="directory-clear" onClick={clearFilters}>
                  Effacer
                </button>
              )}
            </div>

            <div className="directory-fields">
              <div className="directory-field directory-field-search">
                <div className="directory-search">
                  <Search size={16} />
                  <input
                    id="dir-search"
                    type="text"
                    placeholder="Rechercher..."
                    value={filters.q}
                    onChange={(e) => updateFilter('q', e.target.value)}
                  />
                </div>
              </div>

              <div className="directory-field">
                <select id="dir-specialty" aria-label="Spécialité" value={filters.specialty} onChange={(e) => updateFilter('specialty', e.target.value)}>
                  <option value="">Spécialité</option>
                  {uniqueOptions.specialties.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>

              <div className="directory-field">
                <select id="dir-language" aria-label="Langue" value={filters.language} onChange={(e) => updateFilter('language', e.target.value)}>
                  <option value="">Langue</option>
                  {uniqueOptions.languages.map((l) => <option key={l} value={l}>{langLabel(l)}</option>)}
                </select>
              </div>
            </div>

            {activeFilters.length > 0 && (
              <div className="directory-active-filters">
                {activeFilters.map((f) => (
                  <span key={f.key} className="directory-active-chip">
                    {f.label}
                    <button
                      type="button"
                      className="directory-active-remove"
                      onClick={() => removeFilter(f.key)}
                      aria-label={`Retirer le filtre ${f.label}`}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <main className="directory-main">
        <div className="directory-map-box">
          <div ref={mapRef} className="directory-map-canvas" />
          <div className="directory-map-legend">
            {ROLE_OPTIONS.filter((o) => o.value !== 'all').map((o) => (
              <span key={o.value} className="directory-map-legend-item">
                <span className="directory-map-legend-dot" style={{ background: ROLE_HEX[o.value] }} />
                {o.label}
              </span>
            ))}
            <span className="directory-map-legend-hint">
              <MapPin size={13} /> Cliquez sur un marqueur pour voir le profil
            </span>
          </div>
        </div>

        <div className="directory-results">
            {loading && (
              <div className="directory-status">
                <Loader size={28} className="spin" />
                <span>Chargement de l'annuaire...</span>
              </div>
            )}

            {error && <div className="directory-error" role="alert">{error}</div>}

            {!loading && !error && (
              <>
                {professionals.length === 0 ? (
                  <div className="directory-empty">
                    Aucun professionnel ne correspond à ces critères.
                  </div>
                ) : (
                  <div className="directory-grid">
                    {professionals.map((p) => (
                  <article
                    key={p.id}
                    className="directory-card"
                    style={{ '--card-accent': ROLE_COLORS[p.role] || 'var(--teal)' }}
                  >
                    <div className="directory-card-header">
                      <div className="directory-card-photo-wrap">
                        {p.photoUrl ? (
                          <img src={fileUrl(p.photoUrl)} alt={p.name} className="directory-card-photo" />
                        ) : (
                          <div className="directory-card-avatar" style={{ background: ROLE_COLORS[p.role] || 'var(--teal)' }}>
                            <User size={24} />
                          </div>
                        )}
                      </div>

                      <div className="directory-card-header-info">
                        <h2 className="directory-card-name">{p.name}</h2>
                        <span
                          className="directory-card-role"
                          style={{ background: `${ROLE_COLORS[p.role] || 'var(--teal)'}15`, color: ROLE_COLORS[p.role] || 'var(--teal)' }}
                        >
                          {ROLE_LABELS[p.role] || p.role}
                        </span>
                        <span className={`directory-avail directory-avail--${availOf(p).tone}`}>
                          {availOf(p).label}
                        </span>
                      </div>
                    </div>

                    {(p.city || p.country) && (
                      <div className="directory-card-meta">
                        <MapPin size={14} />
                        <span>{[p.city, p.country].filter(Boolean).join(', ')}</span>
                      </div>
                    )}

                    {p.experienceYears > 0 && (
                      <div className="directory-card-meta">
                        <Award size={14} />
                        <span>{p.experienceYears} an{p.experienceYears > 1 ? 's' : ''} exp.</span>
                      </div>
                    )}

                    {p.role === 'translator' && p.rating > 0 && (
                      <div className="directory-card-rating">
                        <Star size={14} fill="var(--gold)" color="var(--gold)" />
                        <span>{p.rating.toFixed(1)}</span>
                      </div>
                    )}

                    {(p.specialties || []).length > 0 && (
                      <div className="directory-card-chips">
                        {p.specialties.slice(0, 2).map((s) => (
                          <span key={s} className="directory-chip">{s}</span>
                        ))}
                      </div>
                    )}

                    {p.role === 'translator' && (p.langPairs || []).length > 0 && (
                      <div className="directory-card-langs">
                        <Globe size={14} />
                        {p.langPairs.slice(0, 2).map((c, i) => (
                          <span key={i} className="directory-lang">{langLabel(c.source)} → {langLabel(c.target)}</span>
                        ))}
                      </div>
                    )}

                    {p.role !== 'translator' && (p.languages || []).length > 0 && (
                      <div className="directory-card-langs">
                        <Globe size={14} />
                        {p.languages.slice(0, 3).map((l, i) => (
                          <span key={i} className="directory-lang">{langLabel(l)}</span>
                        ))}
                      </div>
                    )}

                    <div className="directory-card-footer">
                      <button
                        type="button"
                        className="directory-card-btn"
                        style={{ background: ROLE_COLORS[p.role] || 'var(--teal)' }}
                        onClick={() => setSelectedPro(p)}
                      >
                        <Eye size={14} /> Voir le profil
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            )}

            {pagination.totalPages > 1 && (
              <div className="directory-pagination">
                <button
                  type="button"
                  className="directory-pagination-btn"
                  onClick={() => updateFilter('page', pagination.page - 1)}
                  disabled={pagination.page === 0}
                >
                  ← Précédent
                </button>
                <span className="directory-pagination-info">
                  Page {pagination.page + 1} / {pagination.totalPages}
                </span>
                <button
                  type="button"
                  className="directory-pagination-btn"
                  onClick={() => updateFilter('page', pagination.page + 1)}
                  disabled={pagination.page >= pagination.totalPages - 1}
                >
                  Suivant →
                </button>
              </div>
            )}
          </>
        )}
        </div>
      </main>

      {selectedPro && (
        <div
          className="dir-modal-backdrop"
          onClick={(e) => { if (e.target === e.currentTarget) setSelectedPro(null); }}
        >
          <div className="dir-modal" role="dialog" aria-modal="true" aria-label={selectedPro.name}>
            <div
              className="dir-modal-head"
              style={{ background: `linear-gradient(135deg, ${ROLE_HEX[selectedPro.role] || ROLE_HEX.translator} 0%, ${ROLE_HEX[selectedPro.role] || ROLE_HEX.translator}dd 100%)` }}
            >
              <button
                type="button"
                className="dir-modal-close"
                onClick={() => setSelectedPro(null)}
                aria-label="Fermer"
              >
                <X size={18} />
              </button>
              <div className="dir-modal-avatar">
                {selectedPro.photoUrl ? (
                  <img src={fileUrl(selectedPro.photoUrl)} alt={selectedPro.name} />
                ) : (
                  <span>{initials(selectedPro.name)}</span>
                )}
              </div>
              <div className="dir-modal-head-info">
                <h2>{selectedPro.name}</h2>
                <span className="dir-modal-role">{ROLE_LABELS[selectedPro.role] || selectedPro.role}</span>
                <span className={`directory-avail directory-avail--${availOf(selectedPro).tone}`}>
                  {availOf(selectedPro).label}
                </span>
              </div>
            </div>

            <div className="dir-modal-body">
              <div className="dir-modal-chips">
                {(selectedPro.city || selectedPro.country) && (
                  <span className="dir-modal-chip">
                    <MapPin size={13} /> {[selectedPro.city, selectedPro.country].filter(Boolean).join(', ')}
                  </span>
                )}
                {selectedPro.experienceYears > 0 && (
                  <span className="dir-modal-chip">
                    <Award size={13} /> {selectedPro.experienceYears} an{selectedPro.experienceYears > 1 ? 's' : ''} d'expérience
                  </span>
                )}
                {selectedPro.rating > 0 && (
                  <span className="dir-modal-chip dir-modal-chip--gold">
                    <Star size={13} fill="currentColor" /> {selectedPro.rating.toFixed(1)} / 5
                  </span>
                )}
              </div>

              {(selectedPro.specialties || []).length > 0 && (
                <div className="dir-modal-section">
                  <h3>Spécialités</h3>
                  <div className="dir-modal-tags">
                    {selectedPro.specialties.map((s) => (
                      <span key={s} className="dir-modal-tag">{s}</span>
                    ))}
                  </div>
                </div>
              )}

              {selectedPro.role === 'translator' && (selectedPro.langPairs || []).length > 0 && (
                <div className="dir-modal-section">
                  <h3>Combinaisons de langues</h3>
                  <div className="dir-modal-tags">
                    {selectedPro.langPairs.map((c, i) => (
                      <span key={i} className="dir-modal-tag">{langLabel(c.source)} → {langLabel(c.target)}</span>
                    ))}
                  </div>
                </div>
              )}

              {selectedPro.role !== 'translator' && (selectedPro.languages || []).length > 0 && (
                <div className="dir-modal-section">
                  <h3>Langues</h3>
                  <div className="dir-modal-tags">
                    {selectedPro.languages.map((l, i) => (
                      <span key={i} className="dir-modal-tag">{langLabel(l)}</span>
                    ))}
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}
